import { FC, useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import { Typography, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { getTheme } from 'src/utils/getTheme';

import { SnackbarProps } from './types';
import { styles } from './styles';

const { theme, isHosTheme } = getTheme();

const StyledSnackbar = styled(Snackbar, { shouldForwardProp: (prop) => prop !== 'isSmallScreen' })(
    ({ isSmallScreen }: { isSmallScreen: boolean }) => ({
        position: 'absolute',
        width: isSmallScreen ? '100%' : '435px',
        zIndex: 1,
        '& .MuiAlert-root': {
            backgroundColor: isHosTheme
                ? theme.palette.common.white
                : theme.palette.background.default,
            display: 'flex',
            alignItems: 'flex-start',
            color: theme.palette.common.black,
            boxShadow: '2px 4px 40px 0 #00000014',
            padding: '20px 16px 26px 16px',
            position: 'relative',
            overflow: 'hidden',
            ...(isHosTheme && {
                borderRadius: theme.custom.borderRadius[23],
                border: `1px solid ${theme.palette.error.main}`,
            }),
        },
    })
);

// Add a styled component for the duration line
const DurationLine = styled('div', {
    shouldForwardProp: (prop) => prop !== 'width' && prop !== 'colour',
})<{ width: string; colour: string }>(({ width, colour }) => ({
    height: '6px',
    backgroundColor: colour,
    width,
    transition: 'width 0.1s linear',
    position: 'absolute',
    bottom: '0',
    left: '0',
}));

interface CustomSnackbarProps extends SnackbarProps {
    duration: number;
}

/**
 * A styled Snackbar that displays an error message and automatically closes
 * after a specified duration visually represented by a decreasing width of a line (DurationLine)
 *
 * Props:
 * - open (boolean): controls visibility
 * - message (string): the content displayed, can be split into two parts by '|'
 * - onClose (function): callback to close snackbar
 * - duration (number): time in seconds before auto-close (default: 5 seconds)
 *
 * Behavior:
 * Displays when open; closes automatically after duration or when the close
 * button is clicked, with a visual countdown
 */

export const CustomSnackbar: FC<CustomSnackbarProps> = ({
    open,
    message,
    onClose,
    duration = 5,
    isSmallScreen,
    rebooker = false,
}) => {
    // translatable string example which is split into two parts'
    // firstPart = header, secondPart = content
    // "Email already exists/Please login to your account or request a new password.",
    const [firstPart, secondPart] = message.split('|');
    // const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [durationLineWidth, setDurationLineWidth] = useState('100%');
    const colour = rebooker ? theme.palette.success.main : theme.palette.error.main;
    const severity = rebooker ? 'success' : 'error';

    useEffect(() => {
        if (open) {
            const totalDuration = duration * 1000;
            let remainingTime = totalDuration;

            const interval = setInterval(() => {
                remainingTime -= 100;
                setDurationLineWidth(`${(remainingTime / totalDuration) * 100}%`);

                if (remainingTime <= 0) {
                    clearInterval(interval);
                    onClose();
                }
            }, 100);

            return () => clearInterval(interval);
        }
        return () => {};
    }, [open, onClose]);

    return (
        <StyledSnackbar
            open={open}
            anchorOrigin={{ vertical: 'top', horizontal: isSmallScreen ? 'center' : 'right' }}
            onClose={onClose}
            isSmallScreen={isSmallScreen}
        >
            <Alert severity={severity}>
                <Box display="flex" flexDirection="row" alignItems="flex-start">
                    <Box display="flex" flexDirection="column" flexGrow={1}>
                        <Typography variant="h3" css={styles.header}>
                            {firstPart}
                        </Typography>
                        {secondPart && <Typography mt={1}>{secondPart}</Typography>}
                    </Box>

                    <Box display="flex" alignItems="flex-start">
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            onClick={onClose}
                            css={styles.closeButton}
                        >
                            <CloseIcon fontSize="medium" />
                        </IconButton>
                    </Box>
                </Box>
                <DurationLine width={durationLineWidth} colour={colour} />
            </Alert>
        </StyledSnackbar>
    );
};
